import { Component, inject, Input, ViewContainerRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LaunchDarklyFlag, LaunchDarklyService } from '@anduin/auth';
import { CollectionsMode, ConfigService } from '@anduin/config';
import { getLoadedResults } from '@anduin/shared';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { StatementRecipientLabel } from '../../models';
import { CommonCollectionsState, GetAlreadyExcludedCollectionsClientsTotal } from '../../state';
import { CollectionsExclusionsStep, CollectionsTrackService } from '../../tracking';
import { ManageExcludedClientsDialogComponent } from '../manage-excluded-clients-dialog/manage-excluded-clients-dialog.component';

@Component({
  selector: 'app-excluded-clients',
  templateUrl: './excluded-clients.component.html',
  styleUrls: ['./excluded-clients.component.scss'],
  standalone: false,
})
export class ExcludedClientsComponent {
  @Input() disabled = false;
  private readonly configService = inject(ConfigService);
  private readonly store = inject(Store);
  private readonly launchDarklyService = inject(LaunchDarklyService);
  private readonly dialog = inject(MatDialog);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly trackService = inject(CollectionsTrackService);

  // trigger request for number of excluded clients and start listening to all updates
  excludedStatementRecipientsAmount$ = this.launchDarklyService
    .getFeatureFlag(LaunchDarklyFlag.ENABLE_COLLECTIONS_V2)
    .pipe(
      switchMap((isEnabled) =>
        isEnabled
          ? getLoadedResults(this.store.select(CommonCollectionsState.getCollectionsSetting('excludedRecipientIds'))).pipe(
              map((recipientIds) => recipientIds.length),
            )
          : this.store
              .dispatch(new GetAlreadyExcludedCollectionsClientsTotal())
              .pipe(switchMap(() => this.store.select(CommonCollectionsState.getAlreadyExcludedCollectionsClientsTotal))),
      ),
    );

  shouldShowManageExcludedClients = !this.configService.disableExcludeFromCollections;

  statementRecipientLabel$: Observable<StatementRecipientLabel> = this.store.select(CommonCollectionsState.getCollectionsMode).pipe(
    map((mode) => (mode === CollectionsMode.CONTACT ? StatementRecipientLabel.CONTACT : StatementRecipientLabel.CLIENT)),
    shareReplay(1),
  );

  /**
   * Open dialog to manage client exclusion statuses.
   */
  openManageExcludedClientsDialog(): void {
    this.trackService.trackWorkflow(CollectionsExclusionsStep.DIALOG_OPENED);
    this.dialog.open(ManageExcludedClientsDialogComponent, { viewContainerRef: this.viewContainerRef });
  }
}
